import { call, put, select, StrictEffect, takeEvery } from "redux-saga/effects";

import { Paginated } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import {
  IDepartment,
  TeamMember
} from "@/jenesys-types/jenesys-entities/config";

import { performDataAction } from "@/services/api";
import { selectActiveCompanyId } from "@/store/features/companies/companies";
import { toastError, toastSuccess } from "@/util/helpers";

import {
  addDepartment,
  AddDepartmentAction,
  addDepartmentFailure,
  addDepartmentSuccess,
  deleteMember,
  DeleteMemberAction,
  deleteMemberFailure,
  deleteMemberSuccess,
  departmentsFailure,
  departmentsSuccess,
  DisconnectMemberAction,
  inviteMember,
  InviteMemberAction,
  inviteMemberFailure,
  InviteMemberPlatformAction,
  inviteMemberSuccess,
  loadDepartments,
  LoadDepartmentsAction,
  loadDisconnectMember,
  loadDisconnectMemberFailure,
  loadDisconnectMemberSuccess,
  loadInviteMember,
  loadInviteMemberFailure,
  loadInviteMemberSuccess,
  loadTeamMembers,
  LoadTeamMembersAction,
  resendEmail,
  ResendEmailAction,
  resendEmailFailure,
  resendEmailSuccess,
  teamMembersFailure,
  teamMembersSuccess,
  updateMember,
  UpdateMemberAction,
  updateMemberFailure,
  updateMemberSuccess
} from "./team";

export function* loadTeamSettingsSaga(
  action: LoadTeamMembersAction
): Generator<StrictEffect, void, any> {
  const { clientId, params } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  const limit = 20;
  try {
    const teamMembersResponse: Paginated<TeamMember> = yield call(
      performDataAction,
      DataType.Team,
      "getMembersWithParams",
      {
        companyId: clientId ?? companyId,
        search: params.searchQuery,
        offset: params.page * limit,
        limit
      }
    );

    yield put(teamMembersSuccess(teamMembersResponse));
  } catch (error) {
    yield put(teamMembersFailure(extractError(error)));
  }
}

export function* loadDepartmentsSaga(
  action: LoadDepartmentsAction
): Generator<StrictEffect, void, any> {
  const { clientId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const departmentsResponse: IDepartment[] = yield call(
      performDataAction,
      DataType.Department,
      "list",
      {
        companyId: clientId ?? companyId
      }
    );

    yield put(departmentsSuccess(departmentsResponse));
  } catch (error) {
    yield put(departmentsFailure(extractError(error)));
  }
}

export function* inviteMemberSaga(
  action: InviteMemberAction
): Generator<StrictEffect, void, any> {
  const { clientId, data, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const inviteMemberResponse = yield call(
      performDataAction,
      DataType.Team,
      "create",
      {
        companyId: clientId ?? companyId
      },
      undefined,
      data
    );
    yield put(inviteMemberSuccess(inviteMemberResponse));
    toastSuccess("Invitation sent successfully");
    onSuccess();
  } catch (error) {
    yield put(inviteMemberFailure(extractError(error)));
    toastError("Failed to send invitation");
  }
}

export function* updateMemberSaga(
  action: UpdateMemberAction
): Generator<StrictEffect, void, any> {
  const { clientId, id, data, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const updateMemberResponse = yield call(
      performDataAction,
      DataType.Team,
      "update",
      {
        id,
        companyId: clientId ?? companyId
      },
      undefined,
      data
    );
    yield put(updateMemberSuccess(updateMemberResponse));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(updateMemberFailure(extractError(error)));
  }
}

export function* deleteMemberSaga(
  action: DeleteMemberAction
): Generator<StrictEffect, void, any> {
  const { id, clientId, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(performDataAction, DataType.Team, "delete", {
      id,
      companyId: clientId ?? companyId
    });
    yield put(deleteMemberSuccess(id));
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(deleteMemberFailure(extractError(error)));
  }
}

export function* addDepartmentSaga(
  action: AddDepartmentAction
): Generator<StrictEffect, void, any> {
  const { clientId, data, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const addDepartmentResponse = yield call(
      performDataAction,
      DataType.Department,
      "create",
      {
        companyId: clientId ?? companyId
      },
      undefined,
      data
    );
    yield put(addDepartmentSuccess(addDepartmentResponse));
    onSuccess(addDepartmentResponse);
  } catch (error) {
    yield put(addDepartmentFailure(extractError(error)));
  }
}

export function* loadDisconnectMemberSaga(
  action: DisconnectMemberAction
): Generator<StrictEffect, void, any> {
  const { clientId, memberId, platform, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(performDataAction, DataType.DisconnectMember, "create", {
      companyId: clientId ?? companyId,
      memberId,
      platform
    });

    yield put(loadDisconnectMemberSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(loadDisconnectMemberFailure(extractError(error)));
  }
}

export function* loadInviteMemberSaga(
  action: InviteMemberPlatformAction
): Generator<StrictEffect, void, any> {
  const { clientId, memberId, platform, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(performDataAction, DataType.InviteMember, "create", {
      companyId: clientId ?? companyId,
      memberId,
      platform
    });

    yield put(loadInviteMemberSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(loadInviteMemberFailure(extractError(error)));
  }
}

export function* resendEmailSaga(
  action: ResendEmailAction
): Generator<StrictEffect, void, any> {
  const { clientId, memberId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(performDataAction, DataType.Team, "resendEmailConfirmation", {
      companyId: clientId ?? companyId,
      memberId
    });

    yield put(resendEmailSuccess());
    toastSuccess("Invitation email has been resent successfully");
  } catch (error) {
    yield put(resendEmailFailure(extractError(error)));
    toastError("Failed to resend the invite");
  }
}

export default function* TeamSettingsSaga() {
  yield takeEvery(loadTeamMembers.type, loadTeamSettingsSaga);
  yield takeEvery(loadDepartments.type, loadDepartmentsSaga);
  yield takeEvery(inviteMember.type, inviteMemberSaga);
  yield takeEvery(updateMember.type, updateMemberSaga);
  yield takeEvery(deleteMember.type, deleteMemberSaga);
  yield takeEvery(addDepartment.type, addDepartmentSaga);
  yield takeEvery(loadDisconnectMember.type, loadDisconnectMemberSaga);
  yield takeEvery(loadInviteMember.type, loadInviteMemberSaga);
  yield takeEvery(resendEmail.type, resendEmailSaga);
}
